import { useEffect, useMemo, useState } from 'react';

import { Button, Checkbox, Dialog, Text, Textarea } from '@poliedro/tamentai';

import { StarEmpty, StarFilled } from './Icons';
import {
  CheckboxWrapper,
  DialogContent,
  FeatureName,
  FieldGroup,
  Footer,
  Heading,
  IconButton,
  Label,
  QuestionPrefix,
  StarsWrapper,
  TitleWrapper,
} from './styles';
import { CSATFeatureProps } from './types';

const TOTAL_STARS = 5;

const CSATFeature = ({
  open,
  setOpen,
  featureName,
  title = 'Como você avalia a sua experiência?',
  questionPrefix = 'O que está achando da',
  commentLabel = 'Nos conte mais:',
  contactLabel = 'Se for necessário, vocês podem entrar em contato comigo sobre este comentário.',
  initialRating = 0,
  initialComment = '',
  initialCanContact = true,
  isSubmitting = false,
  onSubmit,
  onCancel,
}: CSATFeatureProps) => {
  const [rating, setRating] = useState(initialRating);
  const [feedback, setFeedback] = useState(initialComment);
  const [contact, setContact] = useState(initialCanContact);

  useEffect(() => {
    if (open) {
      setRating(initialRating);
      setFeedback(initialComment);
      setContact(initialCanContact);
    }
  }, [open, initialRating, initialComment, initialCanContact]);

  const isSubmitDisabled = useMemo(
    () => rating === 0 || isSubmitting,
    [rating, isSubmitting],
  );

  const handleClose = () => {
    onCancel?.();
    setOpen(false);
  };

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    onSubmit({
      rating,
      feedback,
      contact,
    });

    handleClose();
  };

  return (
    <Dialog.Root
      open={open}
      setOpen={handleClose}
      style={{ width: 'clamp(360px, 42vw, 760px)' }}
    >
      <TitleWrapper>
        <Text
          style={{
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: '28px',
            color: '#202020',
          }}
        >
          {title}
        </Text>
        <Dialog.Header />
      </TitleWrapper>
      <Dialog.Body>
        <DialogContent>
          <Heading>
            <QuestionPrefix>{questionPrefix}</QuestionPrefix>
            <FeatureName>{featureName}</FeatureName>
          </Heading>

          <StarsWrapper>
            {Array.from({ length: TOTAL_STARS }, (_, index) => {
              const currentStar = index + 1;
              const isActive = currentStar <= rating;

              return (
                <IconButton
                  key={currentStar}
                  type="button"
                  aria-label={`${currentStar} estrela${currentStar > 1 ? 's' : ''}`}
                  onClick={() => setRating(currentStar)}
                  disabled={isSubmitting}
                >
                  {isActive ? <StarFilled /> : <StarEmpty />}
                </IconButton>
              );
            })}
          </StarsWrapper>

          <FieldGroup>
            <Label htmlFor="csat-feature-comment">{commentLabel}</Label>
            <Textarea
              id="csat-feature-comment"
              value={feedback}
              onChange={event => setFeedback(event.target.value)}
              disabled={isSubmitting}
            />
          </FieldGroup>

          <CheckboxWrapper>
            <Checkbox
              checked={contact}
              onChange={() => setContact(value => !value)}
              disabled={isSubmitting}
            />
            <Text variant="body-sm">{contactLabel}</Text>
          </CheckboxWrapper>

          <Footer>
            <Button
              type="button"
              variant="neutral"
              size="md"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>

            <Button
              type="button"
              size="md"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              {isSubmitting ? 'Enviando...' : 'Enviar'}
            </Button>
          </Footer>
        </DialogContent>
      </Dialog.Body>
    </Dialog.Root>
  );
};

export default CSATFeature;
